import { NotifyHelper } from 'classes/helpers/notify.helper';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { DataCollectorContext } from 'components/machine/dialogs/installation/steps/data-collector/context';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { ShotRejectionHelper } from 'lib_ts/classes/shot-rejection.helper';
import { SfxName, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { getConfidenceCriteria } from 'lib_ts/enums/shot-rejection.enums';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { ITrainingMsg } from 'lib_ts/interfaces/machine-msg/i-training';
import { ITrainingPitch } from 'lib_ts/interfaces/pitches';
import { useContext, useEffect, useMemo, useState } from 'react';

const ENABLE_SOUND = false;

const TESTING_CRITICAL = false;
const TESTING_LOW_CONFIDENCE = false;

const HISTORY_LENGTH = 10;
// before triggering (e.g. if 4, then 4 is won't trigger anything but 5 would)
const MAX_CONSECUTIVE_ERRORS = 4;
const MAX_TOTAL_ERRORS = 5;

interface IProps {
  pitch: ITrainingPitch;
}

export const DataCollectorTrackingErrors = (props: IProps) => {
  const { machine, playSound, setAutoFire } = useContext(MachineContext);
  const { collection, handleToggleLC } = useContext(DataCollectorContext);

  // newest values at the start instead of end
  const [errorHistory, setErrorHistory] = useState<boolean[]>([]);

  // if the user ignores the warning, it shouldn't show up anymore even if more timeouts occur
  const [ignoreCritical, setIgnoreCritical] = useState(false);
  const [ignoreWarning, setIgnoreWarning] = useState(false);

  useEffect(() => {
    // shows error-specific toasts + returns true if we encounter any errors in training responses
    const handleErrors = (message: ITrainingMsg): boolean => {
      const trackingDevice = machine.tracking_device;
      const { shot, detectionFailed } = message;

      // Timeout or failure to detect
      const noDetectionError = (() => {
        if (detectionFailed) {
          return true;
        }

        if (!shot) {
          return true;
        }

        switch (trackingDevice) {
          case TrackingDevice.RapsodoV3PRO: {
            return !shot.rapsodo_shot_id;
          }
          case TrackingDevice.TrackmanB1: {
            return !shot.trackman_pitch_id;
          }
          case TrackingDevice.TrajektVision: {
            return !shot.msbs_shot_id;
          }
          default: {
            return true;
          }
        }
      })();

      if (noDetectionError) {
        NotifyHelper.warning({
          message_md: `${trackingDevice} was unable to detect the previously fired pitch`,
          delay_ms: 5000,
        });

        return true;
      }

      if (!shot) {
        return true;
      }

      // Ball Inserter timeout
      const biTimeoutError = !!shot.bi_timeout;

      if (biTimeoutError) {
        NotifyHelper.warning({
          message_md: `Ball inserter timeout detected`,
          delay_ms: 5000,
        });

        return true;
      }

      // if allowing, don't check anything
      if (collection.include_low_confidence) {
        return false;
      }

      // Low confidence (skipped if user has toggled on allowing low confidence shots)
      const { reject } = ShotRejectionHelper.filterShot({
        shot: shot,
        criteria: getConfidenceCriteria(props.pitch.priority),
      });

      // if not rejected for confidence reasons, don't show anything
      if (!reject) {
        return false;
      }

      // confidence error detected while not allowing LC shots
      const route = shot.rapsodo_shot_id
        ? HELP_URLS.RAPSODO_ERRORS
        : shot.trackman_pitch_id
          ? HELP_URLS.TRACKMAN_ERRORS
          : undefined;

      NotifyHelper.warning({
        message_md: `${trackingDevice} low confidence detected`,
        delay_ms: 5000,
        buttons: [
          {
            label: 'common.read-more',
            invisible: !route,
            onClick: () => window.open(t('common.intercom-url') + route),
          },
          {
            label: `${
              collection.include_low_confidence ? 'Reject' : 'Allow'
            } Low Confidence Shots`,
            dismissAfterClick: true,
            onClick: () => handleToggleLC(),
          },
        ],
      });

      return true;
    };

    const callback = (event: CustomEvent) => {
      const data = event.detail as ITrainingMsg;

      if (data.success === undefined) {
        // ignore interim training messages (e.g. when individual tracking devices report their own detections)
        return;
      }

      // only final training messages will get past this point
      const encounteredError = handleErrors(data);

      if (encounteredError) {
        setErrorHistory((prev) => {
          const nextValue = prev.slice();

          // add counter to start of list
          nextValue.unshift(true);

          // only keep the most recent entries
          nextValue.filter((_, i) => i < HISTORY_LENGTH);
          return nextValue;
        });
      }
    };

    WebSocketHelper.on(WsMsgType.M2U_TrainingResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_TrainingResponse, callback);
    };
  }, [
    props.pitch.priority,
    machine.tracking_device,
    collection.include_low_confidence,
  ]);

  // critical detection failure
  const isCriticalFailure = useMemo(() => {
    if (TESTING_CRITICAL) {
      return true;
    }

    return (
      errorHistory.findIndex((_, i) => {
        if (i + MAX_CONSECUTIVE_ERRORS > errorHistory.length) {
          // too close to end of list
          return false;
        }

        // many errors all in a row
        return errorHistory
          .slice(i, i + MAX_CONSECUTIVE_ERRORS)
          .every((v) => v === true);
      }) !== -1
    );
  }, [errorHistory]);

  useEffect(() => {
    if (!isCriticalFailure) {
      return;
    }

    // stop the test
    setAutoFire(false);

    // play sound on projector
    if (ENABLE_SOUND) {
      playSound(SfxName.ERROR);
    }
  }, [isCriticalFailure]);

  // low confidence warning
  const showWarning = useMemo(() => {
    if (TESTING_LOW_CONFIDENCE) {
      return true;
    }

    return errorHistory.filter((b) => b).length > MAX_TOTAL_ERRORS;
  }, [errorHistory]);

  if (isCriticalFailure && !ignoreCritical) {
    return (
      <CommonCallout
        text={`${machine.tracking_device} doesn't seem to be detecting pitches. Please check the setup to ensure it functions properly.`}
        actions={[
          {
            label: 'common.ignore',
            color: RADIX.COLOR.WARNING,
            onClick: () => setIgnoreCritical(true),
          },
          {
            invisible: true,
            label: 'common.resume',
            color: RADIX.COLOR.WARNING,
            // resets errors without suppressing for the rest of the session (unlike ignore)
            onClick: () => setErrorHistory([]),
          },
        ]}
      />
    );
  }

  if (showWarning && !ignoreWarning) {
    return (
      <CommonCallout
        text={`${machine.tracking_device} is experiencing detection issues. Please check the setup to ensure it functions properly.`}
        actions={[
          {
            label: 'common.ignore',
            color: RADIX.COLOR.WARNING,
            onClick: () => setIgnoreWarning(true),
          },
        ]}
      />
    );
  }

  return null;
};
